import { Button, MenuItem, Select, Typography } from '@mui/material';
import { AthleteTeamProfile, athleteDisplayName } from '@strength/domain';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../../trpc/react';
import { Drawer, DrawerFooter, DrawerHeader } from '../../../components/drawer';
import { getLocale } from '../../../utils/i18n';
import {
  buildKpiBenchmarkHistoryView,
  formatKpiHistoryDate,
  formatKpiHistoryValue,
  type KpiGridMetric,
} from './kpi-benchmarks-utils';

/**
 * KPI variant of the athlete benchmark history drawer. For a given athlete and
 * grid, it shows the full recorded history of a KPI metric newest-first, the
 * current (latest) value, and an empty state when nothing has been recorded.
 *
 * Mirrors {@link AthleteExerciseBenchmarkHistory}: same drawer chrome, a header
 * metric picker (in place of the rep drawer's exercise picker) and internal
 * data fetching. All ordering / current-value / empty-state / formatting logic
 * lives in `kpi-benchmarks-utils` so it is unit-tested independently of render.
 *
 * Task 4 opens this from the KPI grid via `onSetAthleteId`; pass the athlete,
 * the grid's metrics, the grid id and (optionally) an `initialMetricId` to
 * preselect the metric whose cell was clicked.
 */
export const AthleteKpiBenchmarkHistory = ({
  athlete,
  metrics,
  benchmarkGridId,
  initialMetricId,
  open,
  onOpenChange,
}: {
  athlete: AthleteTeamProfile;
  metrics: KpiGridMetric[];
  benchmarkGridId: string;
  initialMetricId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [selectedMetricId, setSelectedMetricId] = useState<string | null>(
    () => initialMetricId ?? metrics[0]?.id ?? null,
  );

  const historyResult =
    trpc.athleteKpiBenchmarks.fetchKpiBenchmarkHistory.useQuery(
      {
        profileId: athlete.id,
        metricId: selectedMetricId ?? '',
        benchmarkGridId,
      },
      {
        staleTime: 10_000,
        enabled: !!selectedMetricId,
      },
    );

  const view = useMemo(
    () => buildKpiBenchmarkHistoryView(historyResult.data ?? []),
    [historyResult.data],
  );

  return (
    <Drawer open={open} onClose={() => onOpenChange(false)}>
      <DrawerHeader onClose={() => onOpenChange(false)}>
        <Typography variant="h2">{athleteDisplayName(athlete)}</Typography>
        <Select
          fullWidth
          value={selectedMetricId}
          className="my-4"
          inputProps={{ 'aria-label': t('selectMetric') }}
          onChange={(e) => {
            setSelectedMetricId(e.target.value);
          }}
        >
          {metrics.map((metric) => (
            <MenuItem key={metric.id} value={metric.id}>
              {metric.name}
            </MenuItem>
          ))}
        </Select>
      </DrawerHeader>
      <div className="px-8 grow">
        {view.current ? (
          <>
            <div className="gap-0">
              <Typography variant="h4">
                {formatKpiHistoryValue(view.current)}
              </Typography>
              <Typography variant="body2Medium" color="text.secondary">
                {t('current')}
              </Typography>
            </div>

            <ul className="mt-4 list-none space-y-2 p-0">
              {view.entries.map((entry) => (
                <li key={entry.id} className="flex flex-col">
                  <Typography variant="body1" fontWeight="bold">
                    {formatKpiHistoryValue(entry)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {formatKpiHistoryDate(entry.date, getLocale())}
                  </Typography>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <Typography
            variant="body2Medium"
            color="text.secondary"
            role="status"
          >
            {t('noAvailableHistory')}
          </Typography>
        )}
      </div>

      <DrawerFooter className="p-8">
        <Button variant="outlined" onClick={() => onOpenChange(false)}>
          {t('close')}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};
